import { useCallback, useEffect, useState } from 'react'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Trash2, RotateCcw, AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import api from '@/services/api'

type ItemType = 'Lead' | 'Deal' | 'Empresa' | 'Carteira' | 'Proposta' | 'Reunião'
type ApiRecycleType = 'leadInstance' | 'deal' | 'empresa' | 'closerLead' | 'proposta' | 'reuniao'

interface DeletedItem {
  id: string
  apiType: ApiRecycleType
  type: ItemType
  name: string
  deletedBy: string
  deletedAt: string
}

const typeBadgeVariant: Record<ItemType, 'info' | 'warning' | 'default' | 'success'> = {
  Lead: 'info',
  Deal: 'warning',
  Empresa: 'default',
  Carteira: 'success',
  Proposta: 'info',
  Reunião: 'warning',
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function mapRecycleBinResponse(data: {
  leadInstances?: Array<{
    id: string
    nome?: string | null
    empresaNome?: string | null
    deletedAt: string | Date | null
    deletedBy?: string | null
  }>
  deals?: Array<{
    id: string
    nome?: string | null
    deletedAt: string | Date | null
    deletedBy?: string | null
  }>
  empresas?: Array<{
    id: string
    nome?: string | null
    razaoSocial?: string | null
    deletedAt: string | Date | null
    deletedBy?: string | null
  }>
  closerLeads?: Array<{
    id: string
    empresa?: string | null
    contato?: string | null
    deletedAt: string | Date | null
    deletedBy?: string | null
  }>
  propostas?: Array<{
    id: string
    nome?: string | null
    deletedAt: string | Date | null
    deletedBy?: string | null
  }>
  reunioes?: Array<{
    id: string
    tipo?: string | null
    deletedAt: string | Date | null
    deletedBy?: string | null
    deal?: { nome?: string | null } | null
  }>
}): DeletedItem[] {
  const items: DeletedItem[] = []

  for (const row of data.leadInstances ?? []) {
    if (!row.deletedAt) continue
    items.push({
      id: row.id,
      apiType: 'leadInstance',
      type: 'Lead',
      name: row.empresaNome || row.nome || 'Lead',
      deletedBy: row.deletedBy || '—',
      deletedAt: String(row.deletedAt),
    })
  }

  for (const row of data.deals ?? []) {
    if (!row.deletedAt) continue
    items.push({
      id: row.id,
      apiType: 'deal',
      type: 'Deal',
      name: row.nome || 'Negociação',
      deletedBy: row.deletedBy || '—',
      deletedAt: String(row.deletedAt),
    })
  }

  for (const row of data.empresas ?? []) {
    if (!row.deletedAt) continue
    items.push({
      id: row.id,
      apiType: 'empresa',
      type: 'Empresa',
      name: row.nome || row.razaoSocial || 'Empresa',
      deletedBy: row.deletedBy || '—',
      deletedAt: String(row.deletedAt),
    })
  }

  for (const row of data.closerLeads ?? []) {
    if (!row.deletedAt) continue
    items.push({
      id: row.id,
      apiType: 'closerLead',
      type: 'Carteira',
      name: row.empresa || row.contato || 'Lead da carteira',
      deletedBy: row.deletedBy || '—',
      deletedAt: String(row.deletedAt),
    })
  }

  for (const row of data.propostas ?? []) {
    if (!row.deletedAt) continue
    items.push({
      id: row.id,
      apiType: 'proposta',
      type: 'Proposta',
      name: row.nome || 'Proposta',
      deletedBy: row.deletedBy || '—',
      deletedAt: String(row.deletedAt),
    })
  }

  for (const row of data.reunioes ?? []) {
    if (!row.deletedAt) continue
    items.push({
      id: row.id,
      apiType: 'reuniao',
      type: 'Reunião',
      name: row.deal?.nome || row.tipo || 'Reunião',
      deletedBy: row.deletedBy || '—',
      deletedAt: String(row.deletedAt),
    })
  }

  return items.sort(
    (a, b) => new Date(b.deletedAt).getTime() - new Date(a.deletedAt).getTime(),
  )
}

export function RecycleBinTab() {
  const [items, setItems] = useState<DeletedItem[]>([])
  const [loading, setLoading] = useState(true)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  const loadItems = useCallback(async () => {
    setLoading(true)
    try {
      const { data: res } = await api.get<{ success: boolean; data: Parameters<typeof mapRecycleBinResponse>[0] }>(
        '/recycle-bin',
      )
      setItems(mapRecycleBinResponse(res.data ?? {}))
    } catch {
      toast.error('Não foi possível carregar a lixeira')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadItems()
  }, [loadItems])

  const handleRestore = async (item: DeletedItem) => {
    try {
      await api.post('/recycle-bin/restore', { type: item.apiType, id: item.id })
      toast.success('Item restaurado com sucesso!')
      await loadItems()
    } catch {
      toast.error('Erro ao restaurar item')
    }
  }

  const handlePermanentDelete = async (item: DeletedItem) => {
    try {
      await api.post('/recycle-bin/permanent-delete', { type: item.apiType, id: item.id })
      setConfirmDeleteId(null)
      toast.success('Item excluído permanentemente!')
      await loadItems()
    } catch {
      toast.error('Erro ao excluir item')
    }
  }

  const handleEmptyTrash = async () => {
    if (!window.confirm('Excluir permanentemente todos os itens da lixeira?')) return
    try {
      for (const item of items) {
        await api.post('/recycle-bin/permanent-delete', { type: item.apiType, id: item.id })
      }
      toast.success('Lixeira esvaziada!')
      await loadItems()
    } catch {
      toast.error('Erro ao esvaziar lixeira')
    }
  }

  return (
    <div className='glass-card p-0 overflow-hidden'>
      <div className='flex items-center justify-between px-4 py-3 border-b border-[var(--glass-border)]'>
        <h3 className='font-semibold text-[var(--font-color-h3)]' style={{ fontSize: 'var(--font-size-h3)' }}>
          Itens Excluídos
        </h3>
        <Button
          variant='destructive'
          size='sm'
          icon={<Trash2 size={13} />}
          onClick={handleEmptyTrash}
          disabled={items.length === 0 || loading}
        >
          Esvaziar Lixeira
        </Button>
      </div>

      <div className='overflow-x-auto'>
        <table className='w-full'>
          <thead>
            <tr className='border-b border-[var(--glass-border)]'>
              {['Tipo', 'Nome', 'Excluído por', 'Data Exclusão', 'Ações'].map((h) => (
                <th
                  key={h}
                  className='text-left px-4 py-3 text-[var(--font-color-label)] font-medium uppercase tracking-wider whitespace-nowrap'
                  style={{ fontSize: 'var(--font-size-label)' }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className='px-4 py-8 text-center text-[var(--text-muted)] text-[13px]'>
                  Carregando…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={5} className='px-4 py-8 text-center text-[var(--text-muted)] text-[13px]'>
                  Lixeira vazia.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr
                  key={`${item.apiType}-${item.id}`}
                  className='border-b border-[var(--glass-border)] last:border-0 hover:bg-[var(--glass-bg)] transition-colors'
                >
                  <td className='px-4 py-3'>
                    <Badge variant={typeBadgeVariant[item.type]}>{item.type}</Badge>
                  </td>
                  <td className='px-4 py-3'>
                    <span className='font-medium text-[var(--text-primary)] text-[13px]'>{item.name}</span>
                  </td>
                  <td className='px-4 py-3'>
                    <span className='text-[var(--text-muted)] text-[13px]'>{item.deletedBy}</span>
                  </td>
                  <td className='px-4 py-3'>
                    <span className='text-[var(--text-muted)] text-[13px]'>{formatDate(item.deletedAt)}</span>
                  </td>
                  <td className='px-4 py-3'>
                    {confirmDeleteId === item.id ? (
                      <div className='flex items-center gap-1'>
                        <button
                          onClick={() => handlePermanentDelete(item)}
                          className='px-2 py-1 rounded-md bg-[var(--color-danger,#e5484d)] text-white text-[11px] font-medium min-h-[44px] flex items-center'
                        >
                          Confirmar
                        </button>
                        <button
                          onClick={() => setConfirmDeleteId(null)}
                          className='px-2 py-1 rounded-md bg-[var(--glass-bg)] text-[var(--text-muted)] text-[11px] font-medium min-h-[44px] flex items-center'
                        >
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <div className='flex items-center gap-1'>
                        <button
                          onClick={() => handleRestore(item)}
                          className='p-2 rounded-md hover:bg-[var(--glass-bg)] text-[var(--text-muted)] hover:text-[var(--brand-primary)] transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center'
                          title='Restaurar'
                        >
                          <RotateCcw size={14} />
                        </button>
                        <button
                          onClick={() => setConfirmDeleteId(item.id)}
                          className='p-2 rounded-md hover:bg-[var(--glass-bg)] text-[var(--text-muted)] hover:text-[var(--color-danger,#e5484d)] transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center'
                          title='Excluir permanentemente'
                        >
                          <AlertTriangle size={14} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
